import type { Theme } from '@mui/material';
import { useTheme } from '@mui/material';
import React from 'react';
import type { CSSProperties, Interpolation } from 'styled-components';
import { useSelector } from 'react-redux';
import Typography from '../../../../component_library/components/Typography/Typography';
import { TypographyVariant } from '../../../../component_library/components/Typography';
import type { IPropsWithTestId } from '../../../../constants/data-element-tracking-ids';
import {
  GridContainer,
  GridItem,
} from '../../../../component_library/components/Grid';
import {
  ButtonVariant,
  LinkButton,
} from '../../../../component_library/components/Button';
import { BREAKPOINT } from '../../../../component_library/utils/breakpoints';
import type { SettingBasedOnBreakpoints } from '../../../../hooks/use-setting-based-on-breakpoints';
import { useSettingBasedOnBreakpoints } from '../../../../hooks/use-setting-based-on-breakpoints';
import type { IRelatedResourcesData } from '../../../../dotcms/dotcms-models/resources/dotcms-article';
import type { ILinkData } from '../../../../dotcms/dotcms-models/dotcms-link';
import {
  getAvailableColumnsCount,
  GRID_CONFIGS_FOR_BREAKPOINTS,
} from '../../../../component_library/utils/gridHelper';
import type { HtmlFromDotcms } from '../../../../dotcms/dotcms-models/converters-from-dotcms';
import HtmlBox from '../../../../component_library/components/HtmlBox';
import { useGetTheme } from '../../../../hooks/use-get-theme';
import { getRelatedResourcesCount } from '../../../../dotcms/dotcms-models/resources/related-resources-helpers';
import ResourcesPreviewCards from './resources-preview-cards';
import { authSelector } from '../../../../selectors';
import { useIsHalfMagicLoggedIn } from '../../../../hooks/useIsHalfMagicLoggedIn';
import { useThemeToken } from '../../../../hooks/use-get-theme/use-get-theme';

interface IProps {
  headlineHtml?: HtmlFromDotcms;
  noteHtml?: HtmlFromDotcms;
  resources: IRelatedResourcesData;
  maxCountOfResources: SettingBasedOnBreakpoints<number>;
  articleLink?: ILinkData;
  button?: IPropsWithTestId & {
    buttonVariant?: ButtonVariant;
    buttonCtaText: string;
    buttonDestinationLink: string;
  };
}

const ResourcesPreviewModule: React.FC<IProps> = ({
  headlineHtml,
  noteHtml,
  resources,
  maxCountOfResources,
  articleLink,
  button,
}) => {
  const theme = useGetTheme();
  const muiTheme = useTheme();
  const themeToken = useThemeToken();

  const { getSettingBasedOnBreakpoint } =
    useSettingBasedOnBreakpoints(muiTheme);

  const { isAuthenticated } = useSelector(authSelector);
  const { halfMagicLoggedIn } = useIsHalfMagicLoggedIn();
  const atLeastHalfMagicLoggedIn = isAuthenticated || halfMagicLoggedIn;

  const displayedResourcesCount = Math.min(
    getRelatedResourcesCount(resources),
    getSettingBasedOnBreakpoint(maxCountOfResources),
  );
  const displayedResourcesArticles = resources.articlesAsArticles.slice(
    0,
    displayedResourcesCount,
  );
  const displayedResourcesFiles = resources.articlesAsExternalFiles.slice(
    0,
    displayedResourcesCount - displayedResourcesArticles.length,
  );
  const displayedResourcesUrls = resources.articlesAsExternalLinks.slice(
    0,
    displayedResourcesCount -
      displayedResourcesArticles.length -
      displayedResourcesFiles.length,
  );
  const displayedResources: IRelatedResourcesData = {
    articlesAsArticles: displayedResourcesArticles,
    articlesAsExternalFiles: displayedResourcesFiles,
    articlesAsExternalLinks: displayedResourcesUrls,
  };

  const getMaxResourceColumnWidthForOneRow = (maxColumnsCount: number) =>
    Math.floor(maxColumnsCount / displayedResourcesCount);

  const desktopMaxColumnsCount = getAvailableColumnsCount(
    GRID_CONFIGS_FOR_BREAKPOINTS[BREAKPOINT.DESKTOP],
  );
  const tabletMaxColumnsCount = getAvailableColumnsCount(
    GRID_CONFIGS_FOR_BREAKPOINTS[BREAKPOINT.TABLET],
  );
  const tabletMiniMaxColumnsCount = getAvailableColumnsCount(
    GRID_CONFIGS_FOR_BREAKPOINTS[BREAKPOINT.TABLET_MINI],
  );
  const mobileMaxColumnsCount = getAvailableColumnsCount(
    GRID_CONFIGS_FOR_BREAKPOINTS[BREAKPOINT.MOBILE],
  );

  const resourcesBoxStyles: Interpolation<Theme> = {
    marginTop: '40px',
    [muiTheme.breakpoints.down('tablet')]: {
      marginTop: '48px',
    },
  };

  const buttonBoxStyles: CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '40px',
    [muiTheme.breakpoints.down('tablet')]: {
      marginTop: '24px',
    },
  };

  return (
    <GridContainer justifyContent="center" alignItems="flex-start">
      <GridItem
        size={{
          desktop: 8,
          tablet: 10,
          tabletMini: tabletMiniMaxColumnsCount,
          mobile: mobileMaxColumnsCount,
        }}
      >
        {headlineHtml && (
          <Typography
            variant={{
              [BREAKPOINT.DESKTOP]: TypographyVariant.H1,
              [BREAKPOINT.TABLET_MINI]: TypographyVariant.H2,
            }}
            muiTheme={muiTheme}
            theme={theme}
          >
            <HtmlBox html={headlineHtml} />
          </Typography>
        )}
        {noteHtml && (
          <Typography
            variant={TypographyVariant.LARGE_BODY_REGULAR}
            additionalStyles={{ marginTop: '20px', marginBottom: '24px' }}
          >
            <HtmlBox html={noteHtml} />
          </Typography>
        )}
      </GridItem>
      <GridItem
        sx={resourcesBoxStyles}
        size={{
          desktop: desktopMaxColumnsCount,
          tablet: tabletMaxColumnsCount,
          tabletMini: tabletMiniMaxColumnsCount,
          mobile: mobileMaxColumnsCount,
        }}
      >
        <GridContainer justifyContent="space-between" alignItems="flex-start">
          <ResourcesPreviewCards
            data={displayedResources}
            atLeastHalfMagicLoggedIn={atLeastHalfMagicLoggedIn}
            articleLink={articleLink}
            renderCardWrapper={(children) => (
              <GridItem
                className="articles-preview-cards-wrapper"
                sx={{ display: 'flex' }}
                size={{
                  desktop: getMaxResourceColumnWidthForOneRow(
                    desktopMaxColumnsCount,
                  ),
                  tablet: getMaxResourceColumnWidthForOneRow(
                    tabletMaxColumnsCount,
                  ),
                  tabletMini: getMaxResourceColumnWidthForOneRow(
                    tabletMiniMaxColumnsCount,
                  ),
                  mobile: mobileMaxColumnsCount,
                }}
              >
                {children}
              </GridItem>
            )}
          />
        </GridContainer>
      </GridItem>
      {button && (
        <GridItem
          sx={buttonBoxStyles}
          size={{
            desktop: desktopMaxColumnsCount,
            tablet: tabletMaxColumnsCount,
            tabletMini: tabletMiniMaxColumnsCount,
            mobile: mobileMaxColumnsCount,
          }}
        >
          <LinkButton
            to={button.buttonDestinationLink}
            themeToken={themeToken}
            variant={button.buttonVariant ?? ButtonVariant.PRIMARY}
            dataElementId={button.dataElementId}
          >
            {button.buttonCtaText}
          </LinkButton>
        </GridItem>
      )}
    </GridContainer>
  );
};

export default ResourcesPreviewModule;
